import { useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Polygon,
  Circle,
  Tooltip,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import { env } from '@/lib/env';
import { useCommandCenter, selectFilteredAssets } from '../store';
import type { AssetDto } from '../api';

const STATUS_COLOR: Record<AssetDto['status'], string> = {
  ACTIVE: '#22c55e',
  IDLE: '#f59e0b',
  ALERT: '#ef4444',
  OFFLINE: '#6b7280',
  STALE: '#6b7280',
  MAINTENANCE: '#0ea5e9',
};

function pinIcon(asset: AssetDto): L.DivIcon {
  const color = STATUS_COLOR[asset.status];
  const heading = asset.heading ?? 0;
  const pulse = asset.status === 'ACTIVE';
  const html = `
    <div class="asset-pin" style="--pin-color:${color}">
      ${pulse ? '<span class="pin-pulse" style="background:' + color + '"></span>' : ''}
      <svg width="22" height="22" viewBox="0 0 22 22" style="transform: rotate(${heading}deg)">
        <circle cx="11" cy="11" r="6" fill="${color}" stroke="#0a0e1a" stroke-width="2"/>
        <path d="M11 2 L14 8 L11 6 L8 8 Z" fill="${color}" stroke="#0a0e1a" stroke-width="1"/>
      </svg>
    </div>`;
  return L.divIcon({
    html,
    className: 'asset-pin-wrapper',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

const SATELLITE_TILE =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

/**
 * Bridges store selection → live Leaflet map camera from *inside* the
 * MapContainer. Selecting an asset (in the left rail list or on the map) sets
 * `selectedAssetId` in the store; this reacts to that change and pans/zooms
 * (flyTo) the camera to the asset's pin.
 */
function MapController() {
  const map = useMap();
  const selectedId = useCommandCenter((s) => s.selectedAssetId);

  // Focus the selected asset. Coordinates are read imperatively so that live
  // GPS fixes (which mutate `assets` constantly) don't re-trigger a flyTo —
  // only an actual selection change moves the camera.
  useEffect(() => {
    if (!selectedId) return;
    const asset = useCommandCenter.getState().assets[selectedId];
    if (asset?.lat == null || asset?.lng == null) return;
    map.flyTo([asset.lat, asset.lng], Math.max(map.getZoom(), 15), {
      duration: 0.75,
    });
  }, [selectedId, map]);

  return null;
}

export function AssetMap() {
  const mapRef = useRef<L.Map | null>(null);
  const assets = useCommandCenter(selectFilteredAssets);
  const select = useCommandCenter((s) => s.select);
  const selectedId = useCommandCenter((s) => s.selectedAssetId);
  const trail = useCommandCenter((s) => s.trail);
  const geofences = useCommandCenter((s) => s.geofences);
  const layers = useCommandCenter((s) => s.layers);

  const pinned = useMemo(() => assets.filter((a) => a.lat != null && a.lng != null), [assets]);

  // Halt any in-flight pan/zoom (e.g. a flyTo to a selected asset) when the
  // page unmounts — for instance when logging out navigates away from the
  // Command Center. This runs before react-leaflet disposes the map, so the
  // animation can't keep firing frames mid-teardown. Guarded so it is a no-op
  // if the instance has already been released.
  useEffect(() => {
    return () => {
      try {
        mapRef.current?.stop();
      } catch {
        /* map already disposed — nothing to stop */
      }
      mapRef.current = null;
    };
  }, []);

  return (
    <MapContainer
      ref={mapRef}
      center={[25.2048, 55.2708]}
      zoom={11}
      scrollWheelZoom
      preferCanvas
      className="h-full w-full bg-cc-bg"
    >
      <MapController />
      <TileLayer
        url={layers.satellite ? SATELLITE_TILE : env.VITE_CARTO_TILE_URL}
        attribution={layers.satellite ? 'Tiles &copy; Esri' : '&copy; OpenStreetMap &copy; CARTO'}
        subdomains={layers.satellite ? '' : 'abcd'}
      />

      {layers.geofences &&
        geofences.map((gf) =>
          gf.shape_type === 'Polygon' ? (
            <Polygon
              key={gf.id}
              positions={(
                (gf.geometry as { coordinates?: number[][][] }).coordinates?.[0] ?? []
              ).map(([lng, lat]) => [lat, lng] as [number, number])}
              pathOptions={{
                color: gf.color,
                weight: 1,
                fillOpacity: 0.05,
              }}
            >
              <Tooltip>{gf.name}</Tooltip>
            </Polygon>
          ) : (
            <Circle
              key={gf.id}
              center={(() => {
                const c = (gf.geometry as { center?: number[] }).center ?? [0, 0];
                return [c[1], c[0]] as [number, number];
              })()}
              radius={(gf.geometry as { radius_m?: number }).radius_m ?? 100}
              pathOptions={{
                color: gf.color,
                weight: 1,
                fillOpacity: 0.05,
              }}
            >
              <Tooltip>{gf.name}</Tooltip>
            </Circle>
          ),
        )}

      {pinned.map((asset) => (
        <Marker
          key={asset.id}
          position={[asset.lat!, asset.lng!]}
          icon={pinIcon(asset)}
          opacity={selectedId && selectedId !== asset.id ? 0.3 : 1}
          eventHandlers={{
            click: () => select(asset.id),
          }}
        >
          <Tooltip direction="top" offset={[0, -10]} opacity={1} className="cc-tooltip">
            <div className="font-mono text-xs">
              <div className="font-semibold">{asset.name}</div>
              <div className="text-cc-muted">{asset.status}</div>
              <div>{asset.speed_kmh != null ? `${asset.speed_kmh.toFixed(0)} km/h` : 'idle'}</div>
            </div>
          </Tooltip>
          <Popup>
            <div className="font-mono text-xs">
              <div className="font-semibold">{asset.name}</div>
              <div className="text-cc-muted">
                {asset.code} · {asset.plate_number ?? '—'}
              </div>
              <button
                onClick={() => select(asset.id)}
                className="mt-2 border border-cc-border px-2 py-0.5 uppercase text-cc-accent"
              >
                Open detail
              </button>
            </div>
          </Popup>
        </Marker>
      ))}

      {trail && trail.points.length > 1 && (
        <Polyline
          positions={trail.points.map((p) => [p.lat, p.lng] as [number, number])}
          pathOptions={{
            color: '#22d3ee',
            weight: 2,
            opacity: 0.9,
          }}
        />
      )}
    </MapContainer>
  );
}
